import { StrictMode } from 'react'
import { logEnvironmentSanity } from './lib/envSanityCheck';

logEnvironmentSanity();
import { createRoot } from 'react-dom/client'
import { BrowserRouter } from 'react-router-dom'
import { QueryClient, QueryClientProvider, QueryCache } from '@tanstack/react-query'
import * as Sentry from '@sentry/react'
import { AuthProvider } from './contexts/AuthContext'
import { PWAProvider } from './contexts/PWAContext'
import { logout } from './lib/auth'
import type { ApiError } from './lib/api'
import './index.css'
import App from './App'

// Initialize Sentry
Sentry.init({
  dsn: import.meta.env.VITE_SENTRY_DSN,
  environment: import.meta.env.MODE,
  enableLogs: true,
  integrations: [
    // #2932: `error` only. `warn` was forwarding the text of every warning to
    // Sentry, and warn lines in this codebase have carried raw user IDs.
    Sentry.consoleLoggingIntegration({ levels: ['error'] }),
  ],
  // #2932: MUST stay false. `true` makes Sentry collect the user's IP address
  // (an identifier under GDPR) from every event, while /privacy describes error
  // monitoring as "optional" — a control that does not exist. Nothing here calls
  // Sentry.setUser, so no diagnostic context is lost by turning it off. Mobile
  // pins the same value explicitly (mobile/src/lib/sentry.ts) — it had been
  // relying on the SDK default, which is not the same as declining.
  sendDefaultPii: false,
})

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      retry: (failureCount, error) => {
        const apiError = error as unknown as ApiError;
        // Never retry 401s - they won't succeed
        if (apiError?.status === 401) return false;
        // Retry other errors once
        return failureCount < 1;
      },
    },
    mutations: {
      onError: (error) => {
        const apiError = error as unknown as ApiError;
        // Automatically logout on any 401 from mutations
        if (apiError?.status === 401) {
          console.log('[QueryClient] 401 detected in mutation, triggering logout');
          logout(queryClient);
        }
      },
    },
  },
  // Global query error handler (alternative to per-query onError)
  // Using queryCache to catch all query errors including retries
  queryCache: new QueryCache({
    onError: (error, query) => {
      const apiError = error as unknown as ApiError;
      // Automatically logout on any 401 from queries EXCEPT:
      // - The initial /me check (we want to stay on public pages if not authenticated)
      // - When already on public pages (login, waitlist, invite pages)
      // - Members-only content errors (specific error codes from backend)
      if (apiError?.status === 401) {
        // Check if this is a members-only content error (not a session expiry)
        const isMembersOnlyError = 
          apiError.code === 'MEMBERS_ONLY_LOGIN_REQUIRED' ||
          apiError.code === 'MEMBERS_ONLY_ACCESS_DENIED';
        
        const isInitialMeCheck = query.queryKey[0] === 'me';
        const currentPath = window.location.pathname;
        // #2846: '/playground' removed — it is no longer a public route. It is
        // a dev-only design-system showcase (registered only on non-production
        // builds; see App.tsx), so it does not belong in the public-page
        // 401→logout-suppression list.
        // #3167: '/signin' is the browser-origin magic-link path (renders
        // MagicLoginPage); it must suppress 401→logout during token consumption
        // exactly as '/magic-login' does.
        const isPublicPage = ['/', '/magic-login', '/signin', '/waitlist', '/clubs', '/events', '/privacy', '/terms', '/about', '/faq'].some(path =>
          currentPath === path || currentPath.startsWith('/invite/')
        );
        
        // Don't trigger logout for members-only content - let the UI handle it
        if (isMembersOnlyError) {
          console.log('[QueryClient] 401 for members-only content, not logging out');
          return;
        }
        
        // Issue #604 Phase 2: Check if user exists before triggering logout
        // Guest users hitting auth endpoints should NOT trigger logout
        const currentUser = queryClient.getQueryData(['me']);
        
        if (!currentUser) {
          // Guest user got 401 on auth-required query (shouldn't happen with enabled guards, but safe)
          console.info('[QueryClient] 401 for guest on auth query – ignoring logout');
          return;
        }
        
        // Only trigger logout if this is NOT the initial me check on a public page
        if (!isInitialMeCheck || !isPublicPage) {
          // We thought we were logged in, but backend says no → real session expiry
          console.warn('[QueryClient] Session expired, logging out');
          logout(queryClient);
        } else {
          console.log('[QueryClient] 401 on /me check for public page, ignoring');
        }
      }
    },
  }),
})

// Register service worker for PWA functionality
if ('serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    navigator.serviceWorker
      .register('/sw.js')
      .then(registration => {
        console.log('[PWA] Service worker registered:', registration.scope);
      })
      .catch(error => {
        console.log('[PWA] Service worker registration failed:', error);
      });
  });
}

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <AuthProvider>
          <PWAProvider>
            <App />
          </PWAProvider>
        </AuthProvider>
      </BrowserRouter>
    </QueryClientProvider>
  </StrictMode>,
)
